import { useCallback, useRef, useState } from 'react';
import { WIDGET_DRAG_THRESHOLD } from './widgetConstants';

const INTERACTIVE_SELECTOR =
  'button, a, input, textarea, select, [role="button"], .MuiButtonBase-root, [data-no-widget-drag]';

/**
 * Pointer drag for floating widget chrome (sprite launcher or chat panel header).
 */
export default function useFloatingDrag({
  onMove,
  dragZoneHeight = null,
  disabled = false,
}) {
  const targetRef = useRef(null);
  const dragRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handlePointerDown = useCallback(
    (event) => {
      if (disabled || event.button !== 0 || !targetRef.current) {
        return;
      }

      const interactive = event.target.closest(INTERACTIVE_SELECTOR);
      if (interactive && interactive !== targetRef.current) {
        return;
      }

      const rect = targetRef.current.getBoundingClientRect();
      if (dragZoneHeight != null && event.clientY - rect.top > dragZoneHeight) {
        return;
      }

      dragRef.current = {
        pointerId: event.pointerId,
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
        startX: event.clientX,
        startY: event.clientY,
        moved: false,
      };

      targetRef.current.setPointerCapture(event.pointerId);
    },
    [disabled, dragZoneHeight]
  );

  const handlePointerMove = useCallback(
    (event) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) {
        return;
      }

      const deltaX = event.clientX - drag.startX;
      const deltaY = event.clientY - drag.startY;

      if (!drag.moved) {
        if (Math.hypot(deltaX, deltaY) < WIDGET_DRAG_THRESHOLD) {
          return;
        }
        drag.moved = true;
        setIsDragging(true);
      }

      onMove({
        x: event.clientX - drag.offsetX,
        y: event.clientY - drag.offsetY,
      });
    },
    [onMove]
  );

  const finishPointerInteraction = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) {
      return false;
    }

    if (targetRef.current?.hasPointerCapture(event.pointerId)) {
      targetRef.current.releasePointerCapture(event.pointerId);
    }

    const wasDrag = drag.moved;
    dragRef.current = null;
    setIsDragging(false);
    return wasDrag;
  }, []);

  return {
    targetRef,
    isDragging,
    handlePointerDown,
    handlePointerMove,
    finishPointerInteraction,
  };
}
